<template>
  <div class="flex h-[calc(100vh-72px)] w-full flex-col overflow-hidden px-4 pb-2">
    <div class="mb-2 flex w-full flex-row items-center justify-center gap-1">
      <BaseIconButton
        icon="keyboard_arrow_left"
        color="standard"
        shape="square"
        size="M"
        width="narrow"
        :disabled="!hasPreviousSlide"
        @click="previousSlide"
      ></BaseIconButton>
      <div
        v-for="(slide, index) in slides"
        :key="slide.id"
        class="rounded-medium group relative flex aspect-8/5 shrink-0 grow-0 cursor-pointer flex-col items-center justify-center overflow-hidden"
        :class="[
          currentSlideIndex == index &&
            'bg-secondary-container text-on-secondary-container h-18 p-2',
          currentSlideIndex != index &&
            'text-on-surface-variant bg-surface-container-lowest h-14 p-1',
        ]"
        @click="selectSlide(index)"
      >
        <BaseIcon v-if="slide.icon" :size="currentSlideIndex == index ? '24' : '16'">{{
          slide.icon
        }}</BaseIcon>
        <span
          :class="[
            currentSlideIndex == index && 'text-body-medium',
            currentSlideIndex != index && 'text-body-small',
          ]"
          class="truncate overflow-hidden text-ellipsis whitespace-nowrap"
          style="max-width: 100%"
        >
          {{ slide.title }}
        </span>
        <div
          v-if="currentSlideIndex == index"
          class="bg-secondary absolute bottom-0 left-1/2 h-1 w-1/4 -translate-x-1/2 rounded-t-[3px]"
        ></div>
        <BaseIcon
          v-if="
            dialogRole === 'coachee' &&
            slide.type !== DialogSlideType.StartSlide &&
            slide.type !== DialogSlideType.ObjectiveSlide
          "
          :size="currentSlideIndex == index ? '18' : '16'"
          class="absolute"
          :class="[
            currentSlideIndex == index && 'text-on-secondary-container top-1 right-1',
            currentSlideIndex != index && 'top-0.5 right-0.5',
            currentSlideIndex != index && slide.finished && 'text-on-surface-variant',
            currentSlideIndex != index && !slide.finished && 'text-outline-variant',
          ]"
          >{{ slide.finished ? 'check_circle' : 'circle' }}</BaseIcon
        >

        <BaseStateLayer :type="currentSlideIndex == index ? 'secondary-container' : 'surface'" />
      </div>
      <BaseIconButton
        icon="keyboard_arrow_right"
        color="standard"
        shape="square"
        size="M"
        width="narrow"
        :disabled="!hasNextSlide"
        @click="nextSlide"
      ></BaseIconButton>
    </div>
    <div class="z-10 mx-auto flex min-h-0 w-full max-w-[1920px] grow flex-row justify-between">
      <div
        class="bg-surface-container-lowest rounded-large h-full w-full grow overflow-y-auto px-10 py-5"
        :class="[dialog.phase === DialogPhase.Conversation && 'animate-moving-glow']"
      >
        <RouterView :key="`${dialog.id}-${dialog.phase}`" />
      </div>
      <div>
        <DialogSideSection
          v-if="showDialogSideSection"
          :dialog="dialog"
          :dialog-slide-id="currentSlide?.id"
          :is-editable="isEditable"
          :coachee-id="coacheeId"
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, provide } from 'vue'
  import BaseIconButton from '../../../core/components/base/button/icon-button/BaseIconButton.vue'
  import BaseIcon from '../../../core/components/base/icon/BaseIcon.vue'
  import BaseStateLayer from '../../../core/components/base/state-layer/BaseStateLayer.vue'
  import type { FeedbackTopic } from '../../../core/models/feedback-topic'
  import {
    dialogKey,
    dialogRoleKey,
    DialogSlideType,
    feedbackTopicsKey,
    useDialogNavigation,
    type DialogRole,
  } from '../composable/use-dialog-navigation'
  import DialogSideSection from './DialogSideSection.vue'
  import { DialogPhase, type Dialog } from '@/core/models/dialog/dialog'

  interface Props {
    dialog: Dialog
    feedbackTopics: ReadonlyArray<FeedbackTopic & { savedAt?: string }>
    isEditable: boolean
    coacheeId: string
    dialogRole: DialogRole
  }
  const props = defineProps<Props>()

  const {
    slides,
    currentSlideIndex,
    selectSlide,
    currentSlide,
    nextSlide,
    previousSlide,
    hasPreviousSlide,
    hasNextSlide,
  } = useDialogNavigation(
    computed(() => props.feedbackTopics),
    true,
    props.dialog.phase,
    props.dialogRole,
  )

  const showDialogSideSection = computed(() => {
    return (
      currentSlide.value?.type !== DialogSlideType.StartSlide &&
      currentSlide.value?.type !== DialogSlideType.ObjectiveSlide
    )
  })

  provide(dialogKey, props.dialog)
  provide(feedbackTopicsKey, props.feedbackTopics)
  provide(dialogRoleKey, props.dialogRole)
</script>
